import type {
  DocumentListResult,
  DocumentStatus,
  DocumentType,
  DocumentUes,
  KnowledgeDocument,
  KnowledgeDocumentDetail,
  UesStatus,
  UploadDocumentsResult,
} from "../../domain/entities/document";
import type {
  DocumentListModel,
  DocumentModel,
  DocumentUesModel,
  UploadFilesResponseModel,
} from "../models/document-model";

const STATUS_VALUES: DocumentStatus[] = [
  "uploaded",
  "processing",
  "extracting_metadata",
  "completed",
  "failed",
];
const TYPE_VALUES: DocumentType[] = ["pdf", "doc", "text", "image", "tabular"];

const toStatus = (raw: string | undefined): DocumentStatus =>
  raw && (STATUS_VALUES as readonly string[]).includes(raw)
    ? (raw as DocumentStatus)
    : "uploaded";

const toType = (raw: string): DocumentType =>
  (TYPE_VALUES as readonly string[]).includes(raw)
    ? (raw as DocumentType)
    : "text";

const UES_STATUSES: UesStatus[] = ["queued", "running", "completed", "failed"];
const toUesStatus = (s: string | undefined): UesStatus =>
  UES_STATUSES.includes(s as UesStatus) ? (s as UesStatus) : "queued";

const toDocument = (model: DocumentModel): KnowledgeDocument => ({
  id: model._id ?? "",
  kbId: model.kb_id,
  userId: model.user_id,
  name: model.name,
  path: model.path,
  documentType: toType(model.document_type),
  status: toStatus(model.status),
});

export const toDocumentList = (
  model: DocumentListModel,
): DocumentListResult => ({
  items: model.items.map(toDocument),
  total: model.total,
});

export const toDocumentDetail = (
  model: DocumentModel,
): KnowledgeDocumentDetail => ({
  ...toDocument(model),
  createdAt: model.created_at ?? null,
  updatedAt: model.updated_at ?? null,
  metadata: model.metadata
    ? {
        docType: model.metadata.doc_type,
        domain: model.metadata.domain,
        docCode: model.metadata.doc_code ?? null,
        titles: model.metadata.titles ?? [],
        topics: model.metadata.topics ?? [],
        entities: model.metadata.entities ?? [],
        validFrom: model.metadata.valid_from ?? null,
        validTo: model.metadata.valid_to ?? null,
        keyEntities: model.metadata.key_entities,
        domainSpecificMetadata: model.metadata.domain_specific_metadata,
      }
    : null,
});

export const toDocumentUes = (model: DocumentUesModel): DocumentUes => ({
  ues: model.ues
    ? {
        docTypeId: model.ues.doc_type_id,
        docTypeKey: model.ues.doc_type_key,
        docTypeName: model.ues.doc_type_name,
        schemaVersion: model.ues.schema_version,
        extractedAt: model.ues.extracted_at,
        fields: model.ues.fields,
      }
    : null,
  stale: model.stale,
  status: toUesStatus(model.status),
});

export const toUploadResult = (
  model: UploadFilesResponseModel,
): UploadDocumentsResult => ({
  uploaded: model.uploaded.map((u) => ({
    id: u.id,
    name: u.name,
    path: u.path,
    documentType: u.document_type,
  })),
  skipped: (model.skipped ?? []).map((s) => ({
    id: s.id,
    name: s.name,
    reason: s.reason,
  })),
  failed: (model.failed ?? []).map((f) => ({
    filename: f.filename,
    error: f.error,
  })),
  total: model.total,
});
